import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/layout/Sidebar.css';

const Sidebar = ({ 
  sections, 
  activeSection, 
  setActiveSection, 
  isProCalculator,
  onSaveProject,
  onManageProjects,
  setCalculatorVersion,
  sectionCompletionStatus = {}
}) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  // Filter out the "all" section
  const filteredSections = sections ? sections.filter(section => section.id !== 'all') : [];
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const isActivePro = (sectionId) => {
    return isProCalculator && activeSection === sectionId;
  };
  
  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2 className="sidebar-title">The Icing Calculator</h2>
      </div>
      
      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          <li className={`sidebar-menu-item ${isActive('/') ? 'active' : ''}`}>
            <Link to="/" className="sidebar-link">
              <span className="sidebar-icon">🏠</span>
              <span className="sidebar-text">Home</span>
            </Link>
          </li>
          
          <li className={`sidebar-menu-item ${isActive('/basic') ? 'active' : ''}`}>
            <Link to="/basic" className="sidebar-link">
              <span className="sidebar-icon">🧮</span>
              <span className="sidebar-text">Basic Calculator</span>
            </Link>
          </li>
          
          {/* Pro Calculator Link - Always Visible */}
          <li className={`sidebar-menu-item ${isActive('/pro') ? 'active' : ''}`}>
            <Link to="/pro" className="sidebar-link">
              <span className="sidebar-icon">⚡</span>
              <span className="sidebar-text">Pro Calculator</span>
            </Link>
            
            {/* Submenu only shown when actually on the Pro Calc page */}
            {isProCalculator && user && filteredSections && (
              <ul className="sidebar-submenu">
                {filteredSections.map((section, index) => {
                  // Get completion status for this section
                  const status = sectionCompletionStatus && sectionCompletionStatus[section.id] ? sectionCompletionStatus[section.id] : 'neutral';
                  const statusClass = `status-${status}`;
                  
                  return (
                    <li 
                      key={section.id}
                      className={`sidebar-submenu-item ${isActivePro(section.id) ? 'active' : ''}`}
                    >
                      <button 
                        className="sidebar-submenu-link"
                        onClick={() => setActiveSection(section.id)}
                      >
                        <span 
                          className={`step-number status-${status}`}
                          data-status={status}
                          data-section={section.id}
                          style={{
                            backgroundColor: status === 'completed' ? '#4CAF50' : 
                                                   status === 'incomplete' ? '#F44336' : 
                                                   '#444',
                            color: '#fff',
                            boxShadow: status === 'completed' ? '0 0 5px rgba(76, 175, 80, 0.7)' : 
                                             status === 'incomplete' ? '0 0 5px rgba(244, 67, 54, 0.7)' : 
                                             'none'
                          }}
                        >
                          {index + 1}
                        </span>
                        <span className="sidebar-text">{section.label}</span>
                      </button>
                    </li>
                  );
                })}
                
                {/* Re-enabled Project Management Actions (using onClick) */}
                <li className="sidebar-submenu-item no-number" onClick={onSaveProject}>
                  <span className="sidebar-submenu-link">
                    Save Project
                  </span>
                </li>
                <li className="sidebar-submenu-item no-number" onClick={onManageProjects}>
                  <span className="sidebar-submenu-link">
                    Manage Projects
                  </span>
                </li>
              </ul>
            )}
          </li>
          
          {/* Shop Page Link */}
          <li className={`sidebar-menu-item ${isActive('/shop') ? 'active' : ''}`}>
            <Link to="/shop" className="sidebar-link">
              <span className="sidebar-icon">🛍️</span>
              <span className="sidebar-text">Shop Recommended</span>
            </Link>
          </li>
          <li className="sidebar-menu-item">
            <a href="https://buy.stripe.com/14kg1sgif77J2EU7st" target="_blank" rel="noopener noreferrer" className="sidebar-link">
              <span className="sidebar-icon">💸</span>
              <span className="sidebar-text">Tip Jar</span>
            </a>
          </li>
          
          {user && (
            <li className={`sidebar-menu-item sidebar-bottom sidebar-account-link`}>
              <Link to="/account" className="sidebar-link">
                <span className="sidebar-icon">👤</span>
                <span className="sidebar-text">My Account</span>
              </Link>
            </li>
          )}
          
          {/* About Us Page Link */}
          <li className={`sidebar-menu-item ${isActive('/about-us') ? 'active' : ''}`}>
            <Link to="/about-us" className="sidebar-link">
              <span className="sidebar-icon">ℹ️</span>
              <span className="sidebar-text">About Us</span>
            </Link>
          </li>
          
          {/* Settings Link - Always Visible but could be conditional later */}
          {/* <li className="sidebar-menu-item">
            <Link to="/settings" className="sidebar-link">
              <span className="sidebar-icon">⚙️</span>
              <span className="sidebar-text">Settings</span>
            </Link>
          </li> */}
           
          {/* Conditional Bottom Links */}
          {user ? (
            <li className="sidebar-menu-item sidebar-bottom">
              <button 
                className="sidebar-link logout-link" 
                onClick={async () => {
                  if (signOut) {
                    const { error } = await signOut(); // AuthContext.signOut handles navigation
                    if (error) {
                      console.error("Logout failed:", error);
                      // Optionally show an error message to the user or handle error
                    }
                    // Navigation is now handled by signOut in AuthContext
                  }
                }}
              >
                <span className="sidebar-icon">🚪</span>
                <span className="sidebar-text">Logout ({user.email})</span>
              </button>
            </li>
          ) : (
            <li className="sidebar-menu-item sidebar-bottom">
              <Link to="/login" className="sidebar-link">
                <span className="sidebar-icon">🔑</span>
                <span className="sidebar-text">Login / Sign Up</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
